import React from 'react';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

interface SecurityInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecurityInfoModal: React.FC<SecurityInfoModalProps> = ({ isOpen, onClose }) => {
  const { encryptionStatus } = useSessionPersistence();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🛡️</span>
            <h2 className="text-xl font-semibold text-gray-900">
              Your Data Security & Privacy
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-gray-300 rounded p-1"
            aria-label="Close security information"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Security Overview */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-green-600">🔒</span>
              <h3 className="font-semibold text-green-800">Best-in-Class Security</h3>
            </div>
            <p className="text-green-700 text-sm">
              End of an Era uses military-grade encryption and privacy-first design to protect your estate planning information. 
              Your data is encrypted locally before being securely transmitted and stored on our protected servers.
            </p>
          </div>

          {/* Current Security Status */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Your Current Security Status</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${encryptionStatus.encrypted ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-700">
                  <strong>Encryption:</strong> {encryptionStatus.encrypted ? 'AES-256-GCM (Military Grade)' : 'Not Encrypted'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${encryptionStatus.secure ? 'bg-green-500' : 'bg-orange-500'}`} />
                <span className="text-sm text-gray-700">
                  <strong>Connection:</strong> {encryptionStatus.secure ? 'Secure HTTPS' : 'Standard Connection'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${encryptionStatus.private ? 'bg-green-500' : 'bg-orange-500'}`} />
                <span className="text-sm text-gray-700">
                  <strong>Privacy Mode:</strong> {encryptionStatus.private ? 'Private Browsing Detected' : 'Regular Browsing'}
                </span>
              </div>
            </div>
          </div>

          {/* How We Protect Your Data */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">How We Protect Your Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">🔐</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Client-Side Encryption</h4>
                    <p className="text-sm text-gray-600">AES-256-GCM encryption happens in your browser before any data is stored.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">🔑</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Unique Session Keys</h4>
                    <p className="text-sm text-gray-600">Each browser session gets a unique encryption key derived from your device fingerprint.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">🔒</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Double Encryption in Transit</h4>
                    <p className="text-sm text-gray-600">Data encrypted with AES-256-GCM locally, then encrypted again with HTTPS/TLS during transmission for bank-level security.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">🛡️</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Protected Storage</h4>
                    <p className="text-sm text-gray-600">Your estate planning data is securely stored and protected with encryption at rest.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">🗑️</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Session Management</h4>
                    <p className="text-sm text-gray-600">Local session data is securely deleted when you log out or session expires.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 mt-1">🔐</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Secure Backup</h4>
                    <p className="text-sm text-gray-600">Your completed estate planning documents are securely backed up and accessible only to you.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What Information We Protect */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">What Information We Protect</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Personal information (name, birthdate, SSN)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                All estate planning answers and decisions
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Beneficiary and executor information
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Asset and financial details
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Progress tracking and completion status
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                Session activity and timestamps
              </li>
            </ul>
          </div>

          {/* Triple-Layer Security */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3">Triple-Layer Security Protection</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
                <div>
                  <h4 className="font-medium text-green-800">Local Encryption (AES-256-GCM)</h4>
                  <p className="text-sm text-green-700">Data encrypted in your browser before leaving your device</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
                <div>
                  <h4 className="font-medium text-green-800">Transit Encryption (HTTPS/TLS)</h4>
                  <p className="text-sm text-green-700">Already-encrypted data is encrypted again during transmission</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
                <div>
                  <h4 className="font-medium text-green-800">Storage Encryption (Azure)</h4>
                  <p className="text-sm text-green-700">Data encrypted at rest on secure Azure infrastructure</p>
                </div>
              </div>
            </div>
            <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-800">
              <strong>Result:</strong> Even if intercepted at any point, your estate planning data remains completely protected.
            </div>
          </div>

          {/* Industry Standards */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Industry-Leading Standards</h3>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-blue-700">
              <div>• AES-256-GCM encryption (same as banks)</div>
              <div>• Web Crypto API implementation</div>
              <div>• HTTPS/TLS + AES double encryption in transit</div>
              <div>• Device fingerprint key derivation</div>
              <div>• Privacy-by-design architecture</div>
              <div>• Secure server-side storage with encryption at rest</div>
            </div>
          </div>

          {/* Privacy Guarantee */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-amber-600">🤝</span>
              <h3 className="font-semibold text-amber-800">Our Privacy Guarantee</h3>
            </div>
            <p className="text-amber-700 text-sm">
              Your estate planning information is deeply personal and sensitive. We guarantee that your data is encrypted 
              locally before transmission and stored securely on our protected servers. Local session data is automatically 
              cleaned when you log out, while your completed documents remain safely backed up and accessible only to you.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Questions about security? Contact our support team.
            </div>
            <button
              onClick={onClose}
              className="bg-cyan-700 hover:bg-cyan-800 text-white px-4 py-2 rounded font-medium text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityInfoModal;